'use client'
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@sarkiai/ui"

interface DialogOptions {
  title?: string
  okText?: string
  cancelText?: string
  severity?: 'error' | 'info' | 'warning' | 'success'
}

interface DialogContextType {
  open: (Component: any, props: any) => Promise<any>
  confirm: (message: string, options?: DialogOptions) => Promise<boolean>
}

const DialogContext = createContext<DialogContextType | null>(null)

export function DialogProvider({ children }: { children: ReactNode }) {
  const [activeDialog, setActiveDialog] = useState<{ Component: any, props: any, resolve: (val: any) => void } | null>(null)
  const [confirmState, setConfirmState] = useState<{ message: string, options?: DialogOptions, resolve: (val: boolean) => void } | null>(null)

  const open = useCallback((Component: any, props: any) => {
    return new Promise((resolve) => {
      setActiveDialog({ Component, props, resolve })
    })
  }, [])

  const confirm = useCallback((message: string, options?: DialogOptions) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ message, options, resolve })
    })
  }, [])

  const closeDialog = (result?: any) => {
    if (activeDialog) {
      activeDialog.resolve(result)
      setActiveDialog(null)
    }
  }

  const handleConfirm = (result: boolean) => {
    if (confirmState) {
      confirmState.resolve(result)
      setConfirmState(null)
    }
  }

  return (
    <DialogContext.Provider value={{ open, confirm }}>
      {children}
      {activeDialog && (
        <activeDialog.Component
          {...activeDialog.props}
          open={!!activeDialog}
          onClose={(res: any) => closeDialog(res)}
        />
      )}
      <AlertDialog open={!!confirmState} onOpenChange={(open) => !open && handleConfirm(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmState?.options?.title || 'Confirmation'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmState?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => handleConfirm(false)}>
              {confirmState?.options?.cancelText || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleConfirm(true)}
              className={confirmState?.options?.severity === 'error' ? 'bg-destructive text-destructive-foreground' : ''}
            >
              {confirmState?.options?.okText || 'OK'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContext.Provider>
  )
}

export const useDialogs = () => {
  const context = useContext(DialogContext)
  if (!context) throw new Error('useDialogs must be used within DialogProvider')
  return context
}
